
import { useEffect, useState } from "react";

import { useDispatch } from "react-redux";

import { useNavigate } from "react-router";

import { Grid, Button, Box, Card, CardContent, Chip, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material";

import UndoIcon from "@mui/icons-material/Undo";

import { openSnackbar } from "../../store/slices/SnackbarSlice";

import { Layout } from "../../components/Layout";

import { ControlarDispositivos, ObtenerDispositivos } from "../../connections/comun/AccesoConnection";

import { Dispositivo } from "../../interfaces/comun/AccesoInterface";
import { CircularProgressIndicator } from "../../components/CircularProgressIndicator";

type FilterType = "all" | "door" | "turnstile";

const actionButtonSx = {
    minHeight: 56,
    justifyContent: "flex-start",
    textAlign: "left",
    px: 1.5,
    borderRadius: 2,
    fontSize: 13,
    "& .MuiButton-startIcon": {
        alignSelf: "flex-start",
        mt: "2px"
    }
};

export const DispositivosPage = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState( true );

    const [doors, setDoors] = useState<Dispositivo[]>([]);
    const [filter, setFilter] = useState<FilterType>("all");

    const getDoorType = ( name: string ): FilterType => {

        const lower = name.toLowerCase();

        if ( lower.includes( "torniquete" ) || lower.includes( "turnstile")  ) {
            return "turnstile";
        }

        return "door";

    };

    const getDoorIcon = ( name: string ) => {

        return getDoorType( name ) === "turnstile" ? "🚧" : "🚪";

    };

    const filteredDoors = doors.filter(( door ) => {

        if ( filter === "all" ) return true;

        return getDoorType( door.doorName ) === filter;

    });

    const controlDoor = async ( doorName: string, doorId: string, controlType: number ) => {

        await ControlarDispositivos({ doorIndexCode: doorId, controlType, doorName }).then( resp => {

            const { success, message } = resp;

            if( success ){

                const newArray = doors.map( (e) => {

                    if( e.doorIndexCode === doorId ){
                        e.doorState = controlType;
                    }

                    return e;
                });

                setDoors( newArray );

                dispatch( openSnackbar({ message: message, variant: 'success' }));     
            }
            else{
                dispatch( openSnackbar({ message: message, variant: 'error' }));     
            }

        });

    };

    useEffect(() => {
        
        async function Obtener() {

            setLoading( true );

            await ObtenerDispositivos().then( resp  => {

                setTimeout(() => {

                    const { success, dispositivos } = resp;

                    if (success) {
                        setDoors( dispositivos );
                    }

                    setLoading( false );

                }, 700);

            });

        }

        Obtener();

    }, []);

    return (

        <Layout breadcrumbs={[ { text: "Administración" }, { text: "Dispositivos" } ]} backgroundPaper={ false }>
        
            <Grid container spacing={3}>
                
                <Grid size={{ xs: 12 }} textAlign={"start"}>

                    <Button
                        color="secondary"
                        startIcon={ <UndoIcon /> }
                        sx={{ mt: 1 }}
                        onClick={ () => navigate("/") }
                    >
                        Regresar
                    </Button>
                </Grid>

            </Grid>

            {
                ( !loading && doors.length !== 0 )
                &&
                    <Box display="flex" justifyContent="end" mt={2}>

                        <ToggleButtonGroup
                            value={ filter }
                            exclusive
                            onChange={ ( e, newFilter ) => {
                                if ( newFilter !== null ) setFilter( newFilter );
                            }}
                            sx={{
                                "& .MuiToggleButton-root": {
                                    px: 2
                                }
                            }}
                        >

                            <ToggleButton value="all">
                                Todos ({ doors.length })
                            </ToggleButton>

                            <ToggleButton value="door">
                                🚪 Puertas ({ doors.filter((d) => getDoorType( d.doorName ) === "door").length })
                            </ToggleButton>

                            <ToggleButton value="turnstile">
                                🚧 Torniquetes ({ doors.filter((d) => getDoorType( d.doorName ) === "turnstile").length })
                            </ToggleButton>

                        </ToggleButtonGroup>

                    </Box>
            }

            {
                loading
                ?
                    <Box height={'70vh'} textAlign={'center'} alignContent={'center'} alignItems={'center'}>
                        <CircularProgressIndicator />
                    </Box>
                :
                    <Box p={3}>

                        <Grid container spacing={3}>

                            {
                                filteredDoors.map(({ doorIndexCode, doorName, doorState, doorNo }) => (

                                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={ doorIndexCode }>

                                        <Card
                                            sx={{
                                                borderRadius: 3,
                                                p: 2,
                                                position: "relative",                                      
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: 12,
                                                    right: 12,
                                                    width: 14,
                                                    height: 14,
                                                    borderRadius: "50%",
                                                    bgcolor:
                                                    ( doorState === 0 || doorState === 2 ) ? "success.main" : "grey.500",
                                                    boxShadow:
                                                        ( doorState === 0 || doorState === 2 )
                                                            ? "0 0 10px rgba(76,175,80,0.9)"
                                                            : "0 0 6px rgba(120,120,120,0.6)"
                                                }}
                                            />

                                            <CardContent sx={{ textAlign: "center" }}>
                                        
                                                <Box fontSize={50}>
                                                    { getDoorIcon( doorName ) }
                                                </Box>

                                                <Typography variant="subtitle1" mt={1} fontWeight={600}>
                                                    { doorName } ({ doorNo })
                                                </Typography>

                                                <Chip
                                                    icon={ <Box component="span"> { ( doorState === 0 || doorState === 2 ) ? "🔓" : "🔒" } </Box> }
                                                    label={ ( doorState === 0 || doorState === 2 ) ? "Desbloqueada" : "Bloqueada" }
                                                    color={ ( doorState === 0 || doorState === 2 ) ? "success" : "default" }
                                                    size="small"
                                                    sx={{
                                                        mt: 1,
                                                        fontWeight: 500,
                                                        p: 2
                                                    }}
                                                />

                                                <Box 
                                                    mt={3} 
                                                    display="grid" 
                                                    gap={1}
                                                    gridTemplateColumns={{
                                                        xs: "1fr",
                                                        sm: "1fr",
                                                        md: "1fr 1fr"
                                                    }}
                                                >

                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        color="success"
                                                        startIcon={ <Box>🔓</Box> }
                                                        sx={ actionButtonSx }
                                                        onClick={ () => controlDoor( doorName, doorIndexCode, 0 ) }
                                                    >
                                                        Permanecer desbloqueada
                                                    </Button>

                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        color="primary"
                                                        startIcon={ <Box>🚪</Box> }
                                                        sx={ actionButtonSx }
                                                        onClick={ () => controlDoor( doorName, doorIndexCode, 2 ) }
                                                    >
                                                        Desbloquear
                                                    </Button>

                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        color="warning"
                                                        startIcon={ <Box>🔒</Box> }
                                                        sx={ actionButtonSx }
                                                        onClick={ () => controlDoor( doorName, doorIndexCode, 1 ) }
                                                    >
                                                        Bloquear
                                                    </Button>

                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        color="error"
                                                        startIcon={ <Box>⛔</Box> }
                                                        sx={ actionButtonSx }
                                                        onClick={ () => controlDoor( doorName, doorIndexCode, 3 ) }
                                                    >
                                                        Permanecer bloqueada
                                                    </Button>

                                                </Box>                                      

                                            </CardContent>

                                        </Card>

                                    </Grid>

                                ))
                            }

                        </Grid>
            
                        { 
                            ( !loading && doors.length === 0 )
                            && 
                                <Typography textAlign="center" mt={4}>
                                    No hay dispositivos
                                </Typography>                    
                        }

                    </Box>
            }

        </Layout>

    );

};